import { isRecord } from "../document/model";
import type { EngineDocument } from "./model";

/** Validate the new schema before the renderer or active document is touched. */
export function assertEngineDocument(
  value: unknown,
): asserts value is EngineDocument {
  const fail = (): never => {
    throw new Error(
      "Повреждённый или неподдерживаемый документ Creator Engine Pro.",
    );
  };
  if (
    !isRecord(value) ||
    value.schemaVersion !== 1 ||
    !Array.isArray(value.pages) ||
    !value.pages.length ||
    !Array.isArray(value.assets) ||
    typeof value.activePageId !== "string"
  )
    return fail();
  const assets = new Set<string>();
  for (const asset of value.assets) {
    if (
      !isRecord(asset) ||
      typeof asset.id !== "string" ||
      !asset.id ||
      assets.has(asset.id) ||
      !["image", "audio"].includes(String(asset.kind)) ||
      typeof asset.source !== "string"
    )
      return fail();
    assets.add(asset.id);
  }
  const ref = (id: unknown) => {
    if (typeof id !== "string" || !assets.has(id)) fail();
  };
  const visit = (node: unknown, ancestors: Set<unknown>) => {
    if (
      !isRecord(node) ||
      ancestors.has(node) ||
      typeof node.id !== "string" ||
      !node.id ||
      ![
        "rectangle",
        "ellipse",
        "circle",
        "line",
        "path",
        "polygon",
        "polyline",
        "text",
        "image",
        "group",
        "extension",
      ].includes(String(node.kind))
    )
      return fail();
    for (const group of [
      "transform",
      "geometry",
      "appearance",
      "text",
      "interaction",
      "effects",
      "layer",
      "media",
    ])
      if (!isRecord(node[group])) return fail();
    const transform = node.transform as Record<string, unknown>;
    for (const key of [
      "x",
      "y",
      "scaleX",
      "scaleY",
      "rotation",
      "skewX",
      "skewY",
    ])
      if (
        key in transform &&
        (typeof transform[key] !== "number" || !Number.isFinite(transform[key]))
      )
        return fail();
    const geometry = node.geometry as Record<string, unknown>;
    for (const key of [
      "width",
      "height",
      "radius",
      "radiusX",
      "radiusY",
      "startX",
      "startY",
      "endX",
      "endY",
      "cropX",
      "cropY",
    ]) {
      if (
        key in geometry &&
        (typeof geometry[key] !== "number" || !Number.isFinite(geometry[key]))
      )
        return fail();
    }
    for (const key of ["points", "warpCorners", "warpEdges"]) {
      if (geometry[key] === undefined) continue;
      if (
        !Array.isArray(geometry[key]) ||
        !(geometry[key] as unknown[]).every(
          (point) =>
            isRecord(point) &&
            typeof point.x === "number" &&
            Number.isFinite(point.x) &&
            typeof point.y === "number" &&
            Number.isFinite(point.y),
        )
      )
        return fail();
    }
    if (
      geometry.commands != null &&
      !Array.isArray(geometry.commands) &&
      node.kind === "path"
    )
      return fail();
    const text = node.text as Record<string, unknown>;
    if (text.content !== undefined && typeof text.content !== "string")
      return fail();
    if (node.compatibility !== undefined) {
      if (!isRecord(node.compatibility)) return fail();
      for (const key of ["extra", "layerExtra"])
        if (
          node.compatibility[key] !== undefined &&
          !isRecord(node.compatibility[key])
        )
          return fail();
    }
    for (const id of Object.values(node.media as Record<string, unknown>))
      ref(id);
    const appearance = node.appearance as Record<string, unknown>;
    for (const key of ["fill", "stroke"]) {
      const paint = appearance[key];
      if (paint === undefined || paint === null || typeof paint === "string")
        continue;
      if (
        !isRecord(paint) ||
        !isRecord(paint.parameters) ||
        ![
          "pattern",
          "linear-gradient",
          "radial-gradient",
          "extension",
        ].includes(String(paint.kind))
      )
        return fail();
      if (paint.assetId !== undefined) ref(paint.assetId);
    }
    const next = new Set(ancestors).add(node);
    if (node.children !== undefined) {
      if (!Array.isArray(node.children)) return fail();
      node.children.forEach((child) => visit(child, next));
    }
    if (node.clip !== undefined && node.clip !== null) visit(node.clip, next);
  };
  const pageIds = new Set<string>();
  for (const page of value.pages) {
    if (
      !isRecord(page) ||
      typeof page.id !== "string" ||
      !page.id ||
      pageIds.has(page.id) ||
      !isRecord(page.size) ||
      !Array.isArray(page.nodes)
    )
      return fail();
    for (const axis of ["width", "height"])
      if (
        typeof page.size[axis] !== "number" ||
        !Number.isFinite(page.size[axis]) ||
        (page.size[axis] as number) <= 0
      )
        return fail();
    for (const key of ["name", "exportName", "unit", "workspaceColor"])
      if (typeof page[key] !== "string") return fail();
    if (typeof page.backgroundVisible !== "boolean") return fail();
    pageIds.add(page.id);
    page.nodes.forEach((node) => visit(node, new Set()));
  }
  if (!pageIds.has(value.activePageId)) return fail();
}
